import {
  DeviceCheckNotes,
  FieldProse,
  NumberedSection,
  obj,
  PanelCard,
  Placeholder,
} from './DossierPrimitives';
import type { SscpDocument } from '@/features/dossier/api';

/**
 * EU MDR Art. 32 Summary of Safety and Clinical Performance (SSCP) renderer.
 *
 * Schema detected by the parent renderer via presence of
 * `s1_device_and_intended_purpose` in the dossier dict (unique to
 * SscpDocument; the EU TD schema uses `s1_device_description` and the EU
 * PMS schema uses `data_collection` — no collision possible).
 *
 * Key features:
 *   - Cover header block (device class, Basic UDI-DI, SSCP revision,
 *     company, EC REP, NB)
 *   - §1 Device and Intended Purpose (5 sub-fields)
 *   - §2 Device Description
 *   - §3 Diagnostic or Therapeutic Alternatives
 *   - §4 Reference to Harmonised Standards and CS
 *   - §5 Clinical Evaluation Summary
 *   - §6 Suggested Profile and Training for Users
 *   - §7 Residual Risks, Undesirable Effects, Warnings and Precautions
 *
 * Gate: gated to `IIb_implantable` and `III` device classes (backend returns
 * 400 for any other class); selector only shown for those classes in
 * DossierDispatchForm.
 *
 * Placeholder discipline: values equal to `__PLACEHOLDER__` or starting with
 * `__PLACEHOLDER__:` are surfaced by the shared Placeholder primitive as
 * amber italic "to complete" chips (identical to EuTdRenderer / EuPmsRenderer).
 */

// ── Types ─────────────────────────────────────────────────────────────────────

interface SubmissionHeader {
  product_name?: string | null;
  company?: string | null;
  ec_rep?: string | null;
  notified_body?: string | null;
  prrc?: string | null;
  srn?: string | null;
  date_of_issue?: string | null;
}

// ── Main component ────────────────────────────────────────────────────────────

export function EuSscpRenderer({
  dossier,
  submissionHeader,
  hideCover = false,
}: {
  dossier: Record<string, unknown>;
  submissionHeader?: SubmissionHeader;
  /** Grouped EU view: the shared header card supplies the identity, so the
   *  per-annex cover block is suppressed. Default false → standalone output is
   *  byte-identical. */
  hideCover?: boolean;
}) {
  return (
    <div className="space-y-4">
      {!hideCover && <SscpCoverBlock dossier={dossier} submissionHeader={submissionHeader} />}
      <DeviceCheckNotes dossier={dossier} />
      <Section1DeviceAndIntendedPurpose dossier={dossier} />
      <Section2DeviceDescription dossier={dossier} />
      <Section3DiagnosticOrTherapeuticAlternatives dossier={dossier} />
      <Section4HarmonisedStandardsAndCs dossier={dossier} />
      <Section5ClinicalEvaluationSummary dossier={dossier} />
      <Section6ProfileAndTraining dossier={dossier} />
      <Section7ResidualRisksAndWarnings dossier={dossier} />
    </div>
  );
}

// ── Cover block ───────────────────────────────────────────────────────────────

function SscpCoverBlock({
  dossier,
  submissionHeader,
}: {
  dossier: Record<string, unknown>;
  submissionHeader?: SubmissionHeader;
}) {
  const deviceClass = dossier['device_class'];
  const basicUdi = dossier['basic_udi_di'];
  const sscpRevision = dossier['sscp_revision'];

  const company = submissionHeader?.company;
  const ecRep = submissionHeader?.ec_rep;
  const notifiedBody = submissionHeader?.notified_body;
  const dateOfIssue = submissionHeader?.date_of_issue;

  return (
    <PanelCard>
      <div className="text-xs tracking-[0.16em] uppercase text-muted font-bold mb-1.5">
        EU MDR Art. 32 SSCP
      </div>
      <p className="text-[11px] text-muted m-0 mb-4">
        Regulation (EU) 2017/745 — Art. 32 / MDCG 2019-9 (Summary of Safety and Clinical
        Performance)
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <CoverCell
          label="Device trade name"
          value={submissionHeader?.product_name}
          fallbackHint="Device trade name"
        />
        <CoverCell label="Device class (EU MDR)" value={deviceClass} />
        <CoverCell
          label="Basic UDI-DI"
          value={basicUdi}
          fallbackHint="Assigned upon registration in EUDAMED"
        />
        <CoverCell
          label="SSCP revision"
          value={sscpRevision}
          fallbackHint="Document revision and date"
        />
        <CoverCell
          label="Manufacturer / legal manufacturer"
          value={company}
          fallbackHint="Manufacturer name and address"
        />
        <CoverCell
          label="EU Authorised Representative (EC REP)"
          value={ecRep}
          fallbackHint="EC REP name and address"
        />
        <CoverCell
          label="Notified Body (NB)"
          value={notifiedBody}
          fallbackHint="NB name and number (if applicable)"
        />
        <CoverCell
          label="Date of issue"
          value={dateOfIssue}
          fallbackHint="Date of issue (YYYY-MM-DD)"
        />
      </div>
    </PanelCard>
  );
}

function CoverCell({
  label,
  value,
  fallbackHint,
}: {
  label: string;
  value: unknown;
  fallbackHint?: string;
}) {
  const v = value == null ? '' : String(value).trim();
  const isPlaceholder = v === '__PLACEHOLDER__' || v.startsWith('__PLACEHOLDER__:');
  const hint = isPlaceholder
    ? v.startsWith('__PLACEHOLDER__:')
      ? v.slice('__PLACEHOLDER__:'.length).trim() || fallbackHint || label
      : fallbackHint || label
    : null;

  return (
    <div className="bg-[#f8f9fa] rounded-md py-2.5 px-3">
      <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-muted mb-0.5">
        {label}
      </div>
      <div className="text-[13px] text-navy">
        {isPlaceholder ? (
          <Placeholder hint={hint} />
        ) : v ? (
          v
        ) : (
          <Placeholder hint={fallbackHint || label} />
        )}
      </div>
    </div>
  );
}

// ── §1 Device and Intended Purpose ────────────────────────────────────────────

const S1_FIELDS: [keyof SscpDocument['s1_device_and_intended_purpose'], string][] = [
  ['device_identification', 'Device identification (§1(a))'],
  ['intended_purpose', 'Intended purpose (§1(b))'],
  ['indications', 'Indications (§1(c))'],
  ['contraindications', 'Contraindications (§1(d))'],
  ['target_patient_population', 'Target patient population (§1(e))'],
];

function Section1DeviceAndIntendedPurpose({
  dossier,
}: {
  dossier: Record<string, unknown>;
}) {
  const s1 = obj(dossier, 's1_device_and_intended_purpose');
  return (
    <NumberedSection prefix="Section" num={1} title="Device and Intended Purpose">
      {S1_FIELDS.map(([key, label]) => {
        const val = s1[key];
        if (val == null) return null;
        return (
          <FieldProse key={key} label={`${label}:`} body={val} fallbackHint={label} />
        );
      })}
    </NumberedSection>
  );
}

// ── §2 Device Description ─────────────────────────────────────────────────────

function Section2DeviceDescription({ dossier }: { dossier: Record<string, unknown> }) {
  const val = dossier['s2_device_description'];
  return (
    <NumberedSection prefix="Section" num={2} title="Device Description">
      <FieldProse
        label="Device description:"
        body={val ?? null}
        fallbackHint="Physical description, materials, and key design features"
      />
    </NumberedSection>
  );
}

// ── §3 Diagnostic or Therapeutic Alternatives ─────────────────────────────────

function Section3DiagnosticOrTherapeuticAlternatives({
  dossier,
}: {
  dossier: Record<string, unknown>;
}) {
  const val = dossier['s3_diagnostic_or_therapeutic_alternatives'];
  return (
    <NumberedSection
      prefix="Section"
      num={3}
      title="Diagnostic or Therapeutic Alternatives"
    >
      <FieldProse
        label="Alternatives available:"
        body={val ?? null}
        fallbackHint="Overview of diagnostic or therapeutic alternatives and their comparison"
      />
    </NumberedSection>
  );
}

// ── §4 Reference to Harmonised Standards and CS ───────────────────────────────

function Section4HarmonisedStandardsAndCs({
  dossier,
}: {
  dossier: Record<string, unknown>;
}) {
  const val = dossier['s4_reference_to_harmonised_standards_and_cs'];
  return (
    <NumberedSection
      prefix="Section"
      num={4}
      title="Reference to Harmonised Standards and Common Specifications"
    >
      <FieldProse
        label="Harmonised standards and CS applied:"
        body={val ?? null}
        fallbackHint="List of harmonised standards and common specifications applied"
      />
    </NumberedSection>
  );
}

// ── §5 Clinical Evaluation Summary ────────────────────────────────────────────

function Section5ClinicalEvaluationSummary({
  dossier,
}: {
  dossier: Record<string, unknown>;
}) {
  const val = dossier['s5_clinical_evaluation_summary'];
  return (
    <NumberedSection prefix="Section" num={5} title="Clinical Evaluation Summary">
      <FieldProse
        label="Clinical evaluation summary:"
        body={val ?? null}
        fallbackHint="Summary of clinical evaluation conclusions per MDR Annex XIV and MDCG 2020-6"
      />
    </NumberedSection>
  );
}

// ── §6 Suggested Profile and Training for Users ───────────────────────────────

function Section6ProfileAndTraining({ dossier }: { dossier: Record<string, unknown> }) {
  const val = dossier['s6_suggested_profile_and_training_for_users'];
  return (
    <NumberedSection
      prefix="Section"
      num={6}
      title="Suggested Profile and Training for Users"
    >
      <FieldProse
        label="User profile and training requirements:"
        body={val ?? null}
        fallbackHint="Intended user profile and any required training or competency requirements"
      />
    </NumberedSection>
  );
}

// ── §7 Residual Risks, Undesirable Effects, Warnings and Precautions ──────────

function Section7ResidualRisksAndWarnings({
  dossier,
}: {
  dossier: Record<string, unknown>;
}) {
  const val = dossier['s7_residual_risks_undesirable_effects_warnings_precautions'];
  return (
    <NumberedSection
      prefix="Section"
      num={7}
      title="Residual Risks, Undesirable Effects, Warnings and Precautions"
    >
      <FieldProse
        label="Residual risks and warnings:"
        body={val ?? null}
        fallbackHint="Residual risks, undesirable effects, warnings, and precautions from risk management"
      />
    </NumberedSection>
  );
}
